import { useFrameBusClient } from '@jusbrasil-web/shared-frame-bus'
import { mountIframeUrlWithCurrentURLParams } from '@jusbrasil-web/shared-utils-url'
import { useCallback, useEffect, useRef, useState } from 'react'

import { boxLockFrameBusEvent } from '../../functions'
import { BoxLockModalEventData } from '../../types'

export type UseBoxLockIframeOptions = Partial<{
  closeOnReconnect?: boolean
  closeOnPopstate?: boolean
  frameBus?: Partial<Parameters<typeof useFrameBusClient>[0]>

  // Prefetch will create a hidden iframe on the first render, automatically pre-loading resources (JS/CSS/GQL queries).
  // However, the iframe will not automatically open until the open method is called.
  //
  // Suggestions for using prefetch:
  // - For the paywall, use 'prefetch' to enhance the user experience, as the paywall is rendered on the first render.
  // - For modals, prefetching can improve the interaction experience, but it can be problematic if there are many
  //   triggers on the same page, as it may cause excessive resource loading and requests.
  //   See: https://jusbrasil.slack.com/archives/C45BDRXL2/p1722971023457189?thread_ts=1722945433.869899&cid=C45BDRXL2
  prefetch?: boolean

  // Prevent the iframe and framebus instance from being created and initialized.
  disabled?: boolean
}>

export type UseBoxLockIframeEventData = BoxLockModalEventData

type OpenOptions = {
  preventAutoShowBoxLockModal?: boolean
  preventNotifyEvents?: boolean
}

export type UseBoxLockIframeReturn = {
  frame: ReturnType<typeof useFrameBusClient>
  ready: boolean
  enabled: boolean
  loading: boolean
  open: (opts?: OpenOptions) => void
  close: () => void
  toggle: () => void
}

export function useBoxLockIframe(
  id: string,
  iframeUrl: string,
  eventData: UseBoxLockIframeEventData,
  opts: UseBoxLockIframeOptions = {}
): UseBoxLockIframeReturn {
  const {
    prefetch: prefetchProp = false,
    closeOnReconnect = true,
    closeOnPopstate = true,
    frameBus = {},
    disabled = false,
  } = opts

  const prefetch = disabled ? false : prefetchProp
  const [enabled, _setEnabled] = useState(prefetch)
  const [ready, setReady] = useState(false)
  const [interested, _setInterested] = useState(false)
  const lastOpenOpts = useRef<OpenOptions>()
  const sentInitializedEvent = useRef(false)

  const frame = useFrameBusClient<
    {
      initBoxLockModal(eventData: UseBoxLockIframeEventData): void
      showBoxLockModal(eventData: UseBoxLockIframeEventData): void
      syncWithCache(eventData: UseBoxLockIframeEventData): void
    },
    {
      setReady(ready: boolean): void
      onInitialize(): void
    }
  >({
    ...frameBus,
    enabled,
    id,
    src: mountIframeUrlWithCurrentURLParams(iframeUrl),
    onConnect() {
      frame.triggerHost('initBoxLockModal', eventData)
      frameBus?.onConnect?.()

      if (!prefetch && !sentInitializedEvent.current) {
        // since we don't re-create the iframe, we need to manually re-trigger the initialize event
        // and it only happens for non-prefetch iframes
        boxLockFrameBusEvent('Initialized', null, eventData)
        sentInitializedEvent.current = true
      }

      boxLockFrameBusEvent('Loaded', null, eventData)
    },
    onReconnect() {
      closeOnReconnect && frame.close()
      frameBus?.onReconnect?.()
    },
    onPopstate() {
      closeOnPopstate && frame.close()
      frameBus?.onPopstate?.()
    },
    listeners: {
      ...frameBus?.listeners,
      onInitialize() {
        if (!sentInitializedEvent.current) {
          boxLockFrameBusEvent('Initialized', null, eventData)
          sentInitializedEvent.current = true
        }
      },
      setReady,
    },
  })

  const openBoxLock = useCallback(
    (opts: OpenOptions = {}) => {
      if (!opts.preventAutoShowBoxLockModal) {
        frame.triggerHost('showBoxLockModal', eventData)
      }
      frame.open()
      if (!opts.preventNotifyEvents) {
        boxLockFrameBusEvent('Showed', null, eventData)
      }
    },
    [frame]
  )

  function setInterested(value: boolean) {
    if (disabled) return
    _setInterested(value)
  }

  function setEnabled(value: boolean) {
    if (disabled) return
    _setEnabled(value)
    if (!enabled && value) {
      boxLockFrameBusEvent('Triggered', null, eventData)
    }
  }

  function open(opts: OpenOptions = {}) {
    if (ready) {
      openBoxLock(opts)
    } else {
      setInterested(true)
      lastOpenOpts.current = opts
    }
  }

  function close() {
    frame.close()
  }

  // auto enable after interested
  useEffect(() => {
    if (!enabled && interested) {
      setEnabled(true)
    }
  }, [enabled, interested])

  // auto open after interested
  useEffect(() => {
    if (!enabled) return
    if (interested && ready) {
      setInterested(false)
      openBoxLock(lastOpenOpts.current || {})
      lastOpenOpts.current = undefined
    } else if (interested && !ready) {
      frame.triggerHost('syncWithCache', eventData)
    }
  }, [enabled, interested, ready, openBoxLock])

  // resync prefetch prop in outdated state
  useEffect(() => {
    if (prefetch && !enabled) {
      setEnabled(prefetch)
    }
  }, [prefetch, enabled])

  // auto notify showed event with defaultOpen without .open() call
  // eg: paywall
  useEffect(() => {
    if (ready && enabled && frameBus?.defaultOpen) {
      boxLockFrameBusEvent('Showed', null, eventData)
    }
  }, [ready, enabled, frameBus?.defaultOpen])

  // reset
  useEffect(() => {
    setReady(false)
  }, [id])

  return {
    frame,
    ready,
    loading: interested && !ready,
    enabled,
    open,
    close,
    // we can't use `frame.toggle()`, due to "interesting" logic
    toggle() {
      if (!ready) {
        setInterested(true)
      } else if (frame.isOpen()) {
        close()
      } else {
        open()
      }
    },
  }
}
